import React, { useMemo } from "react";
import { cilInfo } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { CAlert, CBadge, CTooltip } from "@coreui/react";
import { CDataTable } from "@tradingdroid/frontends-components";
import type {
  ApiMcPermutationMetric,
  ApiMcScenarioRow,
  ApiMonteCarloJobResult,
} from "@tradingdroid/frontends-types";

import { ColoredValue } from "@src/components/ColoredValue";
import {
  POWER_VERDICT,
  SCENARIO_VERDICT_META,
  type ScenarioVerdict,
  VERDICT_CLASS,
  formatPValue,
  metricColored,
  metricDirection,
  metricLabel,
  metricWithheldReason,
  powerHelp,
  ratePValue,
  rateScenario,
  scenarioVerdictHelp,
} from "./robustnessFormat";

type McResult = ApiMonteCarloJobResult["data"]["monte_carlo"];

interface Props {
  result: McResult;
  /** Operator's pre-registered max acceptable drawdown %, from the run's config; gates the verdict. */
  maxDrawdownPct?: number | null;
}

/** Monospaced, tabular metric value via ColoredValue. `signed` colors it by sign (for a change). */
const MetricCell: React.FC<{
  value: number | null | undefined;
  metric: string;
  signed?: boolean;
  bold?: boolean;
  size?: "xs" | "s" | "m";
}> = ({ value, metric, signed, bold, size }) => {
  const c = metricColored(value, metric);
  // The change is raw (positive = a larger metric value). For a lower-is-better metric (drawdown,
  // loss_signals) a positive change is worse, so orient before coloring while still showing the raw
  // signed number.
  let forceColor: "win" | "loss" | undefined;
  if (signed && c.value != null && c.value !== 0) {
    const oriented = metricDirection(metric) === "lower" ? -c.value : c.value;
    forceColor = oriented > 0 ? "win" : "loss";
  }
  return (
    <ColoredValue
      value={c.value}
      type={c.kind}
      decimals={c.decimals}
      size={size}
      forceColor={forceColor}
      className={bold ? "fw-bold" : undefined}
    />
  );
};

const ScenarioVerdictIcon: React.FC<{ verdict: ScenarioVerdict; help: string }> = ({
  verdict,
  help,
}) => {
  const meta = SCENARIO_VERDICT_META[verdict];
  return (
    <CTooltip content={`${meta.label}. ${help}`} placement="top">
      <CIcon
        className={`ms-1 ${VERDICT_CLASS[meta.verdict]}`}
        icon={cilInfo}
        size="sm"
        style={{ cursor: "help" }}
      />
    </CTooltip>
  );
};

const InfoHint: React.FC<{ help: string }> = ({ help }) => (
  <CTooltip content={help} placement="top">
    <CIcon className="ms-1 text-info" icon={cilInfo} size="sm" style={{ cursor: "help" }} />
  </CTooltip>
);

const PowerBadge: React.FC<{ power: string }> = ({ power }) => (
  <CTooltip content={powerHelp(power)} placement="top">
    <CBadge
      color={
        POWER_VERDICT[power] === "good"
          ? "success"
          : POWER_VERDICT[power] === "bad"
            ? "danger"
            : "warning"
      }
      className="fw-normal text-capitalize"
    >
      {power}
    </CBadge>
  </CTooltip>
);

/**
 * Colored p-value with the significance traffic-light icon.
 *
 * A non-empty `withheldReason` strips the verdict entirely: the number describes fewer replicates
 * than the experiment planned — either the run itself says it is incomplete, or replicates it did
 * run produced no usable value for this metric — so it is not a significance finding. The reason is
 * then rendered beside the number, because an uncoloured figure on its own reads as a page with
 * nothing to say rather than one refusing to rate a censored set.
 */
const PValueCell: React.FC<{ pValue: number | null; withheldReason?: string }> = ({
  pValue,
  withheldReason,
}) => {
  const verdict = withheldReason ? null : ratePValue(pValue);
  return (
    <span className={verdict ? VERDICT_CLASS[verdict] : undefined}>
      {verdict && (
        <CIcon
          className={`me-1 ${VERDICT_CLASS[verdict]}`}
          icon={cilInfo}
          size="sm"
          title={
            verdict === "good"
              ? "Significant: the real result beats the noise distribution (p ≤ 0.05)."
              : verdict === "warn"
                ? "Marginal: 0.05 < p ≤ 0.10. Treat with caution."
                : "Not significant: the strategy did not separate from noise (p > 0.10)."
          }
        />
      )}
      {formatPValue(pValue)}
      {withheldReason && (
        <span
          className="RobustnessVerdictWithheld text-body-secondary ms-1 small"
          title={`Significance verdict withheld — this p-value covers ${withheldReason}.`}
        >
          ({withheldReason})
        </span>
      )}
    </span>
  );
};

interface PermRow extends ApiMcPermutationMetric {
  key: string;
}

/**
 * The banner every partial run carries, in whichever view is showing it.
 *
 * A partial run publishes real numbers over fewer children than it planned. They are worth showing
 * — salvaging them is the point — but read without this they are indistinguishable from the finding
 * the complete experiment never produced. Rendered by BOTH aggregate views: an execution-stress run
 * cancelled after 3 of 20 scenarios is exactly as misleading as a truncated permutation.
 */
const PartialRunWarning: React.FC<{ result: McResult }> = ({ result }) => {
  if (result.partial !== true) return null;
  const children = result.children;
  // Hoisted rather than inlined: the sentence's own punctuation would otherwise sit on either side
  // of a JSX expression, where the formatter is free to move it to a line of its own.
  const counts = children
    ? `Children that finished: ${children.completed ?? 0} of ${children.total ?? 0}. A child can ` +
      `finish and still leave nothing the distribution can use, so that count is not a completeness ` +
      `check. `
    : "";
  return (
    <CAlert className="RobustnessPartialWarning" color="warning">
      <strong>Partial run — not a result.</strong> The numbers below cover less than the planned
      experiment{result.stop_reason ? ` (${result.stop_reason})` : ""}. {counts}They are kept so the
      compute is not wasted. Ratings and verdicts are withheld — an absent traffic light here means
      the page refuses to rate a short distribution, not that it found nothing to say. An incomplete
      distribution cannot establish what the planned experiment would have found, so re-run it
      before drawing any conclusion.
      {result.partial_salvage_error ? (
        <div className="RobustnessPartialSalvageError mt-2 small">
          Some of the salvage could not be collected: {result.partial_salvage_error}
        </div>
      ) : null}
    </CAlert>
  );
};

const PermutationView: React.FC<{ result: McResult }> = ({ result }) => {
  const perm = result.permutation;

  const rows = useMemo<PermRow[]>(() => {
    if (!perm) return [];
    return Object.entries(perm.per_metric).map(([key, m]) => ({ ...m, key }));
  }, [perm]);

  const bandKeys = useMemo(() => {
    const first = rows[0];
    if (!first) return [];
    return Object.keys(first.synthetic_summary.bands).sort(
      (a, b) => Number(a.slice(1)) - Number(b.slice(1)),
    );
  }, [rows]);

  if (!perm) return <CAlert color="info">No permutation result on this run.</CAlert>;

  // Raw values stay; the rating is withheld. A verdict computed from a truncated set would read
  // as the finding the complete experiment never produced.
  //
  // The headline and the ranking metric's table row show the SAME number: the coordinator copies
  // the ranking metric's p-value and valid_replicates up into the run-level permutation block. So
  // both go through one predicate — reading the run-wide marker alone here would leave a green
  // "significant" headline above a row whose own traffic light had just been stripped.
  const isPartial = result.partial === true;
  // Say that a verdict was refused, not merely absent, and name what it was refused over. A run
  // the run-wide marker did not catch renders no banner, so without this the headline is a bare
  // number with nothing next to it saying a verdict was refused.
  const rankingWithheldReason = metricWithheldReason({
    runIsPartial: isPartial,
    validReplicates: perm.valid_replicates,
    plannedReplicates: perm.n_replicates,
  });
  const rankingVerdict = rankingWithheldReason ? null : ratePValue(perm.p_value);

  const columns = [
    {
      columnKey: "metric",
      columnLabel: "Metric",
      columnStyle: { width: "180px" },
      cellRender: (row: PermRow) => (
        <span className="RobustnessMetricLabel">
          {metricLabel(row.key)}
          <span className="text-body-secondary ms-1" style={{ fontSize: "0.8em" }}>
            ({/* the API's per-metric direction respects a ranking_direction override */}
            {row.direction === "higher" ? "↑ better" : "↓ better"})
          </span>
        </span>
      ),
    },
    {
      columnKey: "real_value",
      columnLabel: "This backtest",
      columnStyle: { width: "120px", textAlign: "right" as const },
      cellRender: (row: PermRow) => <MetricCell value={row.real_value} metric={row.key} bold />,
    },
    {
      columnKey: "synthetic_mean",
      columnLabel: "Noise mean",
      columnStyle: { width: "110px", textAlign: "right" as const },
      cellRender: (row: PermRow) => (
        <MetricCell value={row.synthetic_summary.mean} metric={row.key} />
      ),
    },
    ...bandKeys.map((band) => ({
      columnKey: band,
      columnLabel: `noise ${band}`,
      columnStyle: { width: "90px", textAlign: "right" as const },
      cellRender: (row: PermRow) => (
        <MetricCell value={row.synthetic_summary.bands[band]} metric={row.key} />
      ),
    })),
    {
      columnKey: "p_value",
      columnLabel: "p-value",
      columnStyle: { width: "110px", textAlign: "right" as const },
      cellRender: (row: PermRow) => (
        <PValueCell
          pValue={row.p_value}
          withheldReason={metricWithheldReason({
            runIsPartial: isPartial,
            validReplicates: row.valid_replicates,
            plannedReplicates: perm.n_replicates,
          })}
        />
      ),
    },
    {
      columnKey: "power",
      columnLabel: "Power",
      columnStyle: { width: "110px" },
      cellRender: (row: PermRow) => <PowerBadge power={row.power} />,
    },
  ];

  return (
    <div className="RobustnessPermutation">
      <div className="RobustnessSummary d-flex flex-wrap align-items-center gap-4 mb-3">
        <div className="RobustnessSummaryItem">
          <div className="text-body-secondary small">
            Ranking p-value ({result.ranking_metric})
            <InfoHint help="Share of synthetic noise runs that matched or beat the real result. Low means the edge is unlikely to be noise." />
          </div>
          <div className={`fs-4 fw-bold ${rankingVerdict ? VERDICT_CLASS[rankingVerdict] : ""}`}>
            {formatPValue(perm.p_value)}
            <span className="fs-6 fw-normal ms-2">
              {rankingVerdict === "good"
                ? "significant"
                : rankingVerdict === "warn"
                  ? "marginal"
                  : rankingVerdict === "bad"
                    ? "not significant"
                    : rankingWithheldReason
                      ? `verdict withheld — ${rankingWithheldReason}`
                      : ""}
            </span>
          </div>
        </div>
        <div className="RobustnessSummaryItem">
          <div className="text-body-secondary small">Power</div>
          <div className="fs-5">
            <PowerBadge power={perm.power} />
          </div>
        </div>
        <div className="RobustnessSummaryItem">
          <div className="text-body-secondary small">Valid replicates</div>
          <div className="fs-5">
            {perm.valid_replicates} / {perm.n_replicates}
          </div>
        </div>
        <div className="RobustnessSummaryItem">
          <div className="text-body-secondary small">Exceedances</div>
          <div className="fs-5">{perm.exceedance_count ?? "—"}</div>
        </div>
      </div>

      <CDataTable
        className="RobustnessPermutationTable"
        columns={columns}
        items={rows}
        getRowKey={(row: PermRow) => row.key}
        striped
        small
        hover
        responsive
      />
    </div>
  );
};

const ExecutionStressView: React.FC<{ result: McResult; maxDrawdownPct?: number | null }> = ({
  result,
  maxDrawdownPct,
}) => {
  const table = result.execution_stress?.scenario_table ?? [];
  const baselineMetrics = result.baseline?.metrics ?? {};
  const isPartial = result.partial === true;

  // Metric columns come from the scenario rows (falling back to the baseline), so any result_metrics
  // set renders without a hardcoded list.
  const metricKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const row of result.execution_stress?.scenario_table ?? [])
      for (const k of Object.keys(row.metrics)) keys.add(k);
    if (keys.size === 0) for (const k of Object.keys(result.baseline?.metrics ?? {})) keys.add(k);
    return [...keys];
  }, [result.execution_stress?.scenario_table, result.baseline?.metrics]);

  if (table.length === 0)
    return <CAlert color="info">No execution-stress scenarios on this run.</CAlert>;

  type Row = { key: string; scenarioName: string; row: ApiMcScenarioRow | null };
  const items: Row[] = [
    { key: "__baseline__", scenarioName: "Baseline (unperturbed)", row: null },
    ...table.map((r) => ({
      key: r.child_key,
      scenarioName: r.scenario_name ?? r.child_key,
      row: r,
    })),
  ];

  const perturbationText = (counts: Record<string, number> | null | undefined): string => {
    if (!counts) return "";
    return Object.entries(counts)
      .filter(([, v]) => typeof v === "number" && v !== 0)
      .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
      .join(", ");
  };

  const columns = [
    {
      columnKey: "scenario",
      columnLabel: "Scenario",
      columnStyle: { width: "200px" },
      cellRender: (item: Row) => {
        const perturb = perturbationText(item.row?.perturbation_counts);
        // One economic verdict per scenario row (never the baseline), rated against pre-registered
        // gates — not a per-cell judgement on each metric.
        // Withheld on a partial run, for the same reason the permutation ratings are: the banner
        // above says verdicts are withheld, and a green tick on 3 of 20 scenarios reads as "this
        // survived" for an experiment that never ran the other 17.
        const verdict =
          item.row && !isPartial
            ? rateScenario(item.row.metrics, baselineMetrics, maxDrawdownPct)
            : null;
        return (
          <span className="RobustnessScenarioName">
            {item.row === null ? <strong>{item.scenarioName}</strong> : item.scenarioName}
            {verdict && (
              <ScenarioVerdictIcon verdict={verdict} help={scenarioVerdictHelp(maxDrawdownPct)} />
            )}
            {perturb && <InfoHint help={perturb} />}
          </span>
        );
      },
    },
    ...metricKeys.map((metric) => ({
      columnKey: metric,
      columnLabel: metricLabel(metric),
      columnStyle: { width: "130px", textAlign: "right" as const },
      cellRender: (item: Row) => {
        if (item.row === null) {
          return <MetricCell value={baselineMetrics[metric]} metric={metric} bold />;
        }
        const cell = item.row.metrics[metric];
        // Value neutral (codex: values aren't rated); the change below carries the direction color.
        return (
          <span className="RobustnessStressCell d-inline-flex flex-column align-items-end">
            <MetricCell value={cell?.value} metric={metric} />
            {cell?.delta_vs_baseline != null && (
              <MetricCell value={cell.delta_vs_baseline} metric={metric} signed size="xs" />
            )}
          </span>
        );
      },
    })),
  ];

  return (
    <CDataTable
      className="RobustnessStressTable"
      columns={columns}
      items={items}
      getRowKey={(item: Row) => item.key}
      striped
      small
      hover
      responsive
    />
  );
};

/** Read-only aggregate view of a completed Backtest Robustness (monte_carlo) run. */
export const RobustnessAggregates: React.FC<Props> = ({ result, maxDrawdownPct }) => {
  return (
    <div className="RobustnessAggregates">
      {result.warnings && result.warnings.length > 0 && (
        <>
          {result.warnings.map((w) => (
            <CAlert key={w} color="warning" className="py-2">
              {w}
            </CAlert>
          ))}
        </>
      )}
      {/* Rendered here rather than inside the views: each of them has early returns, and a
          salvaged run that stopped before its baseline landed takes one of them — so a per-view
          banner is absent from precisely the runs that most need it. */}
      <PartialRunWarning result={result} />
      {result.mode === "permutation" ? (
        <PermutationView result={result} />
      ) : (
        <ExecutionStressView result={result} maxDrawdownPct={maxDrawdownPct} />
      )}
    </div>
  );
};
